/**
 * Contact Service for Outlook Add-in
 * Handles contact data fetching from DoNexus API
 */

import apiClient from '../utils/apiClient';

class ContactService {
  constructor() {
    this.contactId = null; // Contact ID will be set dynamically from incident communications
  }

  /**
   * Fetch contact data by ID
   * @returns {Promise<Object>} Contact data object
   */
  async getContact() {
    if (!this.contactId) {
      throw new Error('Contact ID not set. Please ensure incident communications data is loaded first.');
    }

    try {
      const response = await apiClient.get(`/contact/${this.contactId}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch contact data');
      }
    } catch (error) {
      console.error('Error fetching contact data:', error);
      throw error;
    }
  }

  /**
   * Update contact data by ID
   * @param {string} contactId - Contact ID
   * @param {Object} contactData - Contact data to update
   * @returns {Promise<Object>} Updated contact data
   */
  async updateContact(contactId, contactData) {
    try {
      const response = await apiClient.put(`/contact/${contactId}`, contactData);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to update contact data');
      }
    } catch (error) {
      console.error('Error updating contact data:', error);
      throw error;
    }
  }

  /**
   * Get contact ID
   * @returns {string} Current contact ID
   */
  getContactId() {
    return this.contactId;
  }

  /**
   * Set contact ID
   * @param {string} id - Contact ID
   */
  setContactId(id) {
    this.contactId = id;
  }

  /**
   * Search contacts by query
   * @param {string} query - Search query
   * @returns {Promise<Object>} Search results
   */
  async searchContacts(query) {
    try {
      // Use the correct endpoint with full_name parameter
      const queryParams = new URLSearchParams({
        page: '1',
        limit: '25',
        type: 'person',
        withdetails: 'true',
        full_name: query
      });
      
      const response = await apiClient.get(`/contact?${queryParams.toString()}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to search contacts');
      }
    } catch (error) {
      console.error('Error searching contacts:', error);
      throw error;
    }
  }

  /**
   * Create a new contact
   * @param {Object} contactData - Contact data
   * @returns {Promise<Object>} Created contact
   */
  async createContact(contactData) {
    try {
      const response = await apiClient.post('/contact', contactData);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          id: response.data.id,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to create contact');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }
}

// Create singleton instance
const contactService = new ContactService();

export default contactService;
